import { useEffect, useRef, useState } from "react";
import { useLingui } from "@lingui/react/macro";

// Small clipboard affordance for values an admin has to paste elsewhere (licence
// keys, fingerprints). Flips to a check mark for a moment after a successful
// copy; stays silent if the browser refuses the clipboard (insecure context).

const clipboardIcon = (
  <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
    <rect
      x="5.5"
      y="2.5"
      width="8"
      height="10"
      rx="1.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    />
    <path
      d="M10.5 13.5v0a1.5 1.5 0 0 1-1.5 1.5H4a1.5 1.5 0 0 1-1.5-1.5V5A1.5 1.5 0 0 1 4 3.5h0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
    />
  </svg>
);

const checkIcon = (
  <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">
    <path
      d="M3 8.5 6.5 12 13 4.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface CopyButtonProps {
  value: string;
  /** Accessible name — defaults to a generic "Copy". */
  label?: string;
}

export function CopyButton({ value, label }: CopyButtonProps) {
  const { t } = useLingui();
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      return;
    }
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1500);
  };

  const name = label ?? t`Copy`;
  return (
    <button
      type="button"
      className={`copy-btn${copied ? " copied" : ""}`}
      title={copied ? t`Copied` : name}
      aria-label={name}
      onClick={() => void copy()}
    >
      {copied ? checkIcon : clipboardIcon}
    </button>
  );
}
